import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Form,
  Image,
  Input,
  Message,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  TableColumnProps,
  Typography,
  Upload
} from '@arco-design/web-react';
import dayjs from 'dayjs';
import { debounce } from 'lodash';

import './index.scss';

import { get, post } from '../requestNew';
import { API_CONFIG } from '../utils';
import { Item, UploadImage, UploadItem } from './types';

const Option = Select.Option;
const FormItem = Form.Item;
const TextArea = Input.TextArea;

const formItemLayout = {
  labelCol: {
    span: 7
  },
  wrapperCol: {
    span: 15
  }
};

const Medal: React.FC = () => {
  const [form] = Form.useForm<Item>();
  const [visible, setVisible] = useState(false);
  const [visibleBind, setVisibleBind] = useState(false);
  const [medalName, setMedalName] = useState('');
  const [medalType, setMedalType] = useState<string | number>('');
  const [gameSession, setGameSession] = useState<string>('');
  const [pageSize] = useState(20);
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);
  const [dataList, setDataList] = useState<Item[]>([]);
  const [defaultMedalWornUrl, setMedalWornUrl] = useState<UploadImage[]>([]);
  const [defaultMedalUnwornUrl, setMedalUnwornUrl] = useState<UploadImage[]>([]);

  const [loading, setLoading] = useState(false);

  const medalTypeOptions = [
    { label: '全部', value: '' },
    { label: '身份类', value: 1 },
    { label: '比赛类', value: 2 }
  ];

  const [gameSessionOptions, setGameSessionOptions] = useState<{ label: string; value: string }[]>([
    { label: '全部', value: '' }
  ]);
  const [options, setOptions] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [openId, setOpenId] = useState('');
  const refFetchId = useRef(0);

  const formRef = useRef<any>({});
  const medalRef = useRef<any>({});

  const columns: TableColumnProps[] = [
    {
      title: '序号',
      dataIndex: 'title',
      width: 70,
      fixed: 'left',
      render: (text, record, idx) => idx + 1
    },
    {
      title: '勋章名称',
      dataIndex: 'medalName',
      fixed: 'left',
      width: 120
    },
    {
      title: '勋章编码',
      dataIndex: 'medalCode',
      width: 100
    },
    {
      title: '勋章类型',
      dataIndex: 'medalType',
      width: 100,
      render: (text: string) => {
        const map: Record<string, string> = {
          1: '身份类',
          2: '比赛类'
        };
        return map[text] || '';
      }
    },
    {
      title: '勋章图片已佩戴',
      dataIndex: 'medalWornUrl',
      width: 130,
      render: text => <Image style={{ marginRight: 8 }} width={50} height={50} src={text} alt="" />
    },
    {
      title: '勋章图片未佩戴',
      dataIndex: 'medalUnwornUrl',
      width: 130,
      render: text => <Image style={{ marginRight: 8 }} width={50} height={50} src={text} alt="" />
    },
    {
      title: '勋章描述',
      dataIndex: 'medalDescription',
      width: 130
    },
    // {
    //   title: '勋章规则',
    //   dataIndex: 'medalConditions',
    //   width: 130
    // },
    // {
    //   title: '授予名单',
    //   dataIndex: 'createTime',
    //   width: 130
    // },
    {
      title: '是否启用',
      dataIndex: 'isValid',
      width: 130,
      render: (text: number, record) => (
        <Switch
          checked={text === 1}
          onChange={val => {
            changeIsValid(record, val);
          }}
        />
      )
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      width: 120,
      render: text => dayjs(text).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record: Item) => (
        <>
          <Button className="btn" type="text" onClick={() => edit(record)}>
            编辑
          </Button>
          <Button className="btn" type="text" onClick={() => showBindMedal(record)}>
            绑定勋章
          </Button>
          {/* <Popconfirm
            focusLock
            title="提示"
            content="确定删除数据?"
            onOk={() => {
              del(record);
            }}
          >
            <Button className="btn" type="text">
              删除
            </Button>
          </Popconfirm> */}
        </>
      )
    }
  ];

  const titleChange = (val: string) => {
    setMedalName(val);
  };

  const changeIsValid = (record: Item, val: boolean) => {
    post('/medal/base/update', { ...record, isValid: val ? 1 : 0 })
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('操作成功！');
          getList();
        } else {
          Message.error(message);
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      });
  };

  const getList = (pag = {}, newGameSession?: string, newMedalType?: string | number) => {
    const pagination = {
      pageSize,
      pageNum,
      ...pag
    };
    const params: any = { keyword: medalName, ...pagination };
    const currentMedalType = newMedalType !== undefined ? newMedalType : medalType;
    const currentGameSession = newGameSession !== undefined ? newGameSession : gameSession;
    if (currentMedalType !== '') {
      params.medalType = currentMedalType;
    }
    if (currentGameSession !== '') {
      params.gameSession = currentGameSession;
    }
    get('/medal/base/list', params)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          const { list, total } = data;
          setTotal(total);
          setDataList(list || []);
        } else {
          Message.error(message);
        }
      })
      .catch(e => {
        Message.error('网络错误!');
        console.log(e);
      });
  };

  const add = () => {
    formRef.current = {};
    setMedalWornUrl([]);
    setMedalUnwornUrl([]);
    setVisible(true);
    form.setFieldValue('isValid', 1);
  };
  const del = (record: Item) => {
    post('/medal/base/delete', { id: record.id })
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('删除成功！');
          if (dataList.length > 1) {
            getList();
          } else {
            setPageNum(pageNum - 1);
          }
        } else {
          Message.error(message);
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      });
  };

  const edit = (record: Item) => {
    formRef.current = record;
    form.setFieldsValue(record);
    const { medalWornUrl, medalUnwornUrl } = record;
    setMedalWornUrl([
      {
        uid: medalWornUrl,
        name: medalWornUrl,
        url: medalWornUrl
      }
    ]);
    setMedalUnwornUrl([
      {
        uid: medalUnwornUrl,
        name: medalUnwornUrl,
        url: medalUnwornUrl
      }
    ]);
    setVisible(true);
  };
  const editCancel = () => {
    formRef.current = {};
    setVisible(false);
    setMedalWornUrl([]);
    setMedalUnwornUrl([]);
    form.resetFields();
  };
  const onOk = () => {
    form.validate().then(formData => {
      const data = {
        ...formRef.current,
        ...formData
      };
      const url = formRef.current.id ? '/medal/base/update' : '/medal/base/add';
      post(url, data)
        .then((res: any) => {
          const { code, message } = res || {};
          if (code === 200) {
            getList();
            editCancel();
          } else {
            Message.error(message);
          }
        })
        .catch(e => {
          Message.error('网络错误!');
          console.log(e);
        });
    });
  };

  const showBindMedal = (record: Item) => {
    medalRef.current = record;
    setVisibleBind(true);
  };
  const onBindCancel = () => {
    setVisibleBind(false);
    setOpenId('');
  };
  const onBindOk = (grant: number) => {
    if (!openId) {
      Message.error('请选择用户');
      return;
    }
    setLoading(true);
    post('/medal/user/grant', { openId, medalId: medalRef.current.id, grant })
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success(grant === 1 ? '绑定成功！' : '解绑成功！');
        } else {
          Message.error(message);
        }
      })
      .finally(() => {
        setLoading(false);
        onBindCancel();
      });
  };

  const uploadImg = (res?: UploadItem, key?: any) => {
    const { status, response } = res || {};
    if (status === 'done') {
      const { code, data = '' } = response || {};
      if (code === 200) {
        form.setFieldValue(key, data);
      }
    }
  };

  const debouncedFetchUser = useCallback(
    debounce(gameName => {
      refFetchId.current = Date.now();
      const fetchId = refFetchId.current;
      setFetching(true);
      setOptions([]);
      get('/backend/umsUser/list', { gameName, pageNum: 1, pageSize: 20 }).then((res: any) => {
        const { code, data } = res || {};

        if (refFetchId.current === fetchId) {
          setFetching(false);
          if (code === 200) {
            const options = data.list.map((item: { gameName: string; openId: string }) => {
              const { gameName, openId } = item;
              return {
                label: gameName,
                value: openId
              };
            });
            setTotal(total);
            setOptions(options);
          }
        }
      });
    }, 500),
    []
  );

  useEffect(() => {
    getList();
  }, [pageNum]);

  useEffect(() => {
    get('/gameConfig/getUmsGameConfigList').then((res: any) => {
      if (res && res.data) {
        const list = res.data.map((item: any) => {
          const { gameSession } = item;
          return { label: gameSession, value: gameSession };
        });
        setGameSessionOptions([{ label: '全部', value: '' }, ...list]);
      }
    });
  }, []);

  return (
    <div className="composition table-flex">
      <Space style={{ flexWrap: 'wrap' }}>
        <div className="filter-item">
          <Typography.Text>勋章名称:</Typography.Text>
          <Input
            value={medalName}
            onChange={titleChange}
            style={{ width: 190 }}
            allowClear
            onPressEnter={() => getList({ pageNum: 1 })}
          />
        </div>
        <div className="filter-item">
          <Typography.Text>勋章类型:</Typography.Text>
          <Select
            value={medalType}
            onChange={(value) => {
              setMedalType(value);
              getList({ pageNum: 1 }, gameSession, value);
            }}
            style={{ width: 120 }}
          >
            {medalTypeOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>赛季:</Typography.Text>
          <Select
            value={gameSession}
            onChange={(value) => {
              setGameSession(value);
              getList({ pageNum: 1 }, value, medalType);
            }}
            style={{ width: 120 }}
          >
            {gameSessionOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <Button
          className="filter-item"
          type="primary"
          onClick={() => {
            getList({ pageNum: 1 });
          }}
        >
          查询
        </Button>
        <Button className="filter-item" type="primary" onClick={add}>
          新增
        </Button>
      </Space>
      <div className="table-content">
        <Table
          rowKey="id"
          columns={columns}
          data={dataList}
          className="table-scroll"
          pagination={{
            sizeCanChange: false,
            pageSize,
            total,
            showTotal: true,
            current: pageNum,
            onChange: num => {
              setPageNum(num);
            }
          }}
          scroll={{ y: '100%', x: '100%' }}
        />
      </div>
      <Modal
        title={formRef.current.id ? '编辑' : '新增'}
        visible={visible}
        onCancel={editCancel}
        onOk={onOk}
        unmountOnExit
      >
        <Form {...formItemLayout} form={form}>
          <FormItem label="勋章名称" field="medalName" rules={[{ required: true, message: '请输入勋章名称' }]}>
            <Input placeholder="" />
          </FormItem>
          <FormItem label="勋章编码" field="medalCode" rules={[{ required: true, message: '请输入勋章编码' }]}>
            <Input placeholder="" />
          </FormItem>
          <FormItem label="勋章类型" field="medalType" rules={[{ required: true, message: '请选择勋章类型' }]}>
            <Select>
              <Option value={1}>身份类</Option>
              <Option value={2}>比赛类</Option>
            </Select>
          </FormItem>
          <FormItem
            label="勋章图片已佩戴"
            field="medalWornUrl"
            rules={[{ required: true, message: '请上传勋章图片已佩戴' }]}
          >
            <Upload
              multiple={false}
              limit={1}
              imagePreview
              listType="picture-card"
              defaultFileList={defaultMedalWornUrl}
              action={API_CONFIG.uploadUrl}
              onChange={(res: any) => {
                uploadImg(res[0], 'medalWornUrl');
              }}
            />
          </FormItem>
          <FormItem
            label="勋章图片未佩戴"
            field="medalUnwornUrl"
            rules={[{ required: true, message: '请上传勋章图片未佩戴' }]}
          >
            <Upload
              multiple={false}
              limit={1}
              imagePreview
              listType="picture-card"
              defaultFileList={defaultMedalUnwornUrl}
              action={API_CONFIG.uploadUrl}
              onChange={(res: any) => {
                uploadImg(res[0], 'medalUnwornUrl');
              }}
            />
          </FormItem>
          <FormItem label="勋章描述" field="medalDescription" rules={[{ required: true, message: '请输入勋章描述' }]}>
            <TextArea rows={4} />
          </FormItem>
          <FormItem label="勋章规则" field="medalConditions">
            <TextArea rows={4} />
          </FormItem>
          <FormItem label="启用" field="isValid">
            <Select>
              <Option value={1}>启用</Option>
              <Option value={0}>不启用</Option>
            </Select>
          </FormItem>
        </Form>
      </Modal>
      <Modal
        title={'绑定勋章'}
        visible={visibleBind}
        onCancel={onBindCancel}
        footer={
          <>
            <Button loading={loading} onClick={() => onBindOk(1)} type="primary">
              绑定勋章
            </Button>
            <Button loading={loading} onClick={() => onBindOk(0)} type="primary" status="danger">
              解除绑定
            </Button>
            <Button onClick={onBindCancel}>关闭</Button>
          </>
        }
        unmountOnExit
      >
        <Form {...formItemLayout} form={form}>
          <FormItem label="用户名">
            <Select
              showSearch
              options={options}
              placeholder="请输入用户名"
              filterOption={false}
              notFoundContent={
                fetching ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Spin style={{ margin: 12 }} />
                  </div>
                ) : null
              }
              onSearch={debouncedFetchUser}
              onChange={val => {
                setOpenId(val);
              }}
            />
          </FormItem>
          <FormItem label="勋章名称" field="medalId">
            <div>{medalRef.current.medalName}</div>
          </FormItem>
        </Form>
      </Modal>
    </div>
  );
};

export default Medal;
